/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 * DChatModal component
 *
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.38.0
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { TDChatTokenResponse } from '@modules/dchat/types';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import ModalErrorCommon from '@modules/modal/components/ModalError';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import {
	makeSelectAuthDChat,
	makeSelectLoggedIn,
} from '@pages/PageAuth/selectors/authSelectors';

import { Loader } from '../loader/components/Loader';
import { chatId, config, dchatContainerId, defaultChatSrc } from './config';
import { useGetDChatTokenQuery } from './store';

interface IDChatModalProps {
	onModalClose: () => void;
}

export const DChatModal = (props: IDChatModalProps) => {

	const { onModalClose } = props;

	const isAuthorized = useSelector(makeSelectLoggedIn());
	const dChatSettings = useSelector(makeSelectAuthDChat());
	const { modal } = useSelector(makeSelectTranslationCommon());

	const dispatch = useDispatch();

	const [isModalStatusError, setIsModalStatusError] = useState(false);
	const [dChatToken, setDChatToken] = useState<TDChatTokenResponse>({ accessToken: '', expiresIn: 0 });
	const dChatDivRef = useRef<HTMLDivElement | null>(null);

	const dChatTokenPlausible = !!dChatToken.accessToken && !!dChatToken.expiresIn;

	const chatConfig = useMemo(
		() => ({
			token: dChatToken.accessToken,
			backendEndPoint: dChatSettings?.backendEndpoint ?? '',
			channel: dChatSettings?.channel ?? '', //
			customer: dChatSettings?.customer ?? '',
			config,
		}),
		[
			dChatSettings?.backendEndpoint,
			dChatSettings?.channel,
			dChatSettings?.customer,
			dChatToken.accessToken,
		],
	);

	const chatScript = document.getElementById(chatId);

	const {
		data: dChatTokenData,
		isLoading: isDChatTokenLoading,
		isSuccess: isDChatTokenSuccess,
		isError: isDChatTokenError,
		refetch: refetchChatToken,
	} = useGetDChatTokenQuery(undefined, {
		pollingInterval: isModalStatusError ? 0 : dChatToken.expiresIn * 1000,
	});

	const isChatShown = isDChatTokenSuccess && !!dChatToken.accessToken;

	const handleModalClose = useCallback(() => {
		onModalClose();
	}, [onModalClose]);

	const initCallback = useCallback(() => {
		window.hermes && window.hermes.init(chatConfig, dchatContainerId);
	}, [chatConfig]);

	const onDivClick = () => {
		setTimeout(() => {
			if (dChatDivRef?.current?.children.length === 0) {
				handleModalClose();
			}
		}, 100);
	};

	const openErrorModal = useCallback((text?: string, title?: string) => {
		dispatch(modalOpen({
			render: (props: IModalRenderProps) => <ModalErrorCommon
				title={title}
				translation={modal}
				onCloseModal={props.onCloseModal}
			>
				{text}
			</ModalErrorCommon>,
		}));
	}, [dispatch, modal]);

	useEffect(() => {
		if (isDChatTokenError) {
			onModalClose();
			openErrorModal();
		}
	}, [isDChatTokenError, onModalClose, openErrorModal]);

	useEffect(() => {
		if (!isAuthorized) {
			chatScript && document.head.removeChild(chatScript);
		}
	}, [chatScript, isAuthorized]);

	useEffect(() => {
		if (!isDChatTokenLoading && isDChatTokenSuccess && !!dChatTokenData) {
			setDChatToken(dChatTokenData);
		}
	}, [dChatTokenData, isDChatTokenLoading, isDChatTokenSuccess]);

	useEffect(() => {
		if (!chatScript && isChatShown && dChatTokenPlausible && !isModalStatusError) {
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.src = dChatSettings?.chatScriptSrc ?? defaultChatSrc;
			script.id = chatId;
			script.onload = () => initCallback();
			script.onerror = () => {
				console.error('Chat script error');
				setIsModalStatusError(true);
			};
			document.head.appendChild(script);
		}
		if (chatScript && isChatShown && !dChatDivRef?.current?.children.length && !isModalStatusError) {
			refetchChatToken();
			initCallback();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatScript, isChatShown]);

	useEffect(() => {
		if (chatScript && dChatTokenPlausible && !isModalStatusError) {
			try {
				const updatedConfig = { ...chatConfig, token: dChatToken.accessToken };
				window.hermes.update(updatedConfig);
			} catch (e) {
				console.error('Error:', e);
				setIsModalStatusError(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dChatToken.accessToken]);

	return (
			<Loader
				showLoader={isDChatTokenLoading}
			>
				<div
					id={dchatContainerId}
					ref={dChatDivRef}
					onClickCapture={onDivClick}
				/>
			</Loader>
	);
};
